import { BehaviorSubject, map, of, shareReplay, switchMap, tap } from "rxjs";
import { odataGet, odataList } from "./odataApi";
import { getAuthService } from "./auth.service";
import { hasPolicy$ } from "helpers/hasPolicy";
import { initService } from "helpers/initService";

class ClientsService {
  private readonly _refetchAt$ = new BehaviorSubject<Date>(new Date());
  private readonly _getClientsOberservable = new Map<number, ReturnType<typeof this._getClient>>();
  public readonly clients$ = hasPolicy$("IdeallyAdmin").pipe(
    switchMap((isSuper) =>
      isSuper
        ? of([])
        : this._refetchAt$.pipe(
            switchMap(() =>
              odataList("getClients", {}, { noClientId: true })
                .select("id", "name", "hsCompanyId", "tier")
                .orderBy("name")
                .expandAndSelect("team", null, "orgId")
                .expandAndSelect("org", null, "enterpriseId")
                .top(600)
                .$(),
            ),
            tap((c) => c.value && !c.value.length && getAuthService().logout()),
            map((c) => c.value),
            shareReplay(1),
          ),
    ),
    shareReplay(1),
  );

  public searchClients = (search: string) => {
    if (search.length < 1) {
      return of([]);
    }
    return odataList("getClients", {}, { noClientId: true })
      .select("id", "name", "nameClean", "hsCompanyId", "tier")
      .filterSearchOr(["name", "nameClean"], [search])
      .orderBy("name")
      .$()
      .pipe(map((c) => c.value));
  };

  public getClient = (id: number | undefined) => {
    if (id === undefined) {
      return of(null);
    }
    if (!this._getClientsOberservable.has(id)) {
      this._getClientsOberservable.set(id, this._getClient(id));
    }
    return this._getClientsOberservable.get(id)!;
  };
  private readonly _getClient = (id: number) => {
    return odataGet("getClientsOne", { key: id }, { noClientId: true })
      .select("id", "name", "hsCompanyId", "tier")
      .$()
      .pipe(shareReplay(1));
  };
  constructor() {}

  public refetch = () => this._refetchAt$.next(new Date());
}

let clientsService: Readonly<ClientsService> | null = null;

export const getClientsService = () => {
  if (!clientsService) {
    clientsService = initService(() => new ClientsService());
  }
  return clientsService;
};
